/**
 * x402 Facilitator SDK Types
 * Simple, type-safe middleware for API monetization
 */

export type SUINetwork = 'testnet' | 'mainnet'

export type SUIAsset = 'SUI' | 'USDC'

export interface FacilitatorConfig {
  /** Facilitator API key (required for facilitator service communication) */
  apiKey: string
  /** SUI network (testnet or mainnet) */
  network?: SUINetwork
  /** Request timeout in milliseconds */
  timeout?: number
  /** Custom headers to send with requests */
  headers?: Record<string, string>
}

export interface PaymentRequirement {
  /** SUI network */
  network: SUINetwork
  /** Exact amount required (string to avoid precision issues) */
  amount: string
  /** SUI asset (SUI or USDC) */
  asset: SUIAsset
  /** Payee address */
  payTo: string
  /** Human-readable description */
  description?: string
  /** Optional metadata */
  extra?: Record<string, any>
}

export interface PaymentRequirementsResponse {
  /** Error message if payment required */
  error?: string
  /** Array of acceptable payment methods */
  accepts: PaymentRequirement[]
}


export interface VerificationRequest {
  /** Base64 encoded X-PAYMENT header */
  paymentHeader: string
  /** Payment requirements from original request */
  paymentRequirements: PaymentRequirement 
}

export interface VerificationResponse {
  /** Whether payment is valid */
  isValid: boolean
  /** Error reason if invalid */
  invalidReason?: string
  /** Additional details */
  details?: any
}

export interface SettlementRequest {
  /** Base64 encoded X-PAYMENT header */
  paymentHeader: string
  /** Payment requirements from original request */
  paymentRequirements: PaymentRequirement
}

export interface SettlementResponse {
  /** Whether settlement succeeded */
  success: boolean
  /** Transaction hash */
  txHash?: string
  /** Network identifier */
  networkId?: string
  /** Payer address (from transaction) */
  payer?: string
  /** Gas usage details */
  gasUsed?: {
    computationCost: string
    storageCost: string
    storageRebate: string
    totalCost: string
  }
  /** Error message if failed */
  error?: string
  /** Additional details */
  details?: any
}

export interface MiddlewareOptions {
  /** Amount to charge (in smallest unit, e.g. '1000000' for 0.001 SUI) */
  amount: string
  /** SUI asset to use (defaults to SUI) */
  asset?: SUIAsset
  /** Payee address (required) */
  payTo: string
  /** Payment description */
  description?: string
}




export class X402Error extends Error {
  constructor(
    message: string,
    public code: string,
    public statusCode: number = 500
  ) {
    super(message)
    this.name = 'X402Error'
  }
}

export class PaymentRequiredError extends X402Error {
  constructor(
    requirements: PaymentRequirementsResponse,
    public _x402Client?: any,
    public _requirement?: any
  ) {
    super('Payment required', 'PAYMENT_REQUIRED', 402)
    this.name = 'PaymentRequiredError'
    this.requirements = requirements
  }

  requirements: PaymentRequirementsResponse
}

export class FacilitatorError extends X402Error {
  constructor(message: string, public details?: any) {
    super(message, 'FACILITATOR_ERROR', 502)
    this.name = 'FacilitatorError'
  }
}
